import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { cacheKeys, useSubscription } from '../../graphql';
import { useGrippContext } from '../../grippContext';

// Trailing debounce, capped: a burst (a campaign dispatch, a bulk routine run)
// becomes one refetch instead of hundreds, but a steady stream still lands
// within MAX_WAIT rather than being deferred for as long as it keeps coming.
export const LIVE_REFRESH_DEBOUNCE_MS = 750;
export const LIVE_REFRESH_MAX_WAIT_MS = 3000;

/**
 * Keeps every mounted activity list current while it is on screen: any
 * activity created or changed in `workspaceId` (including a new reply, which
 * bumps its activity) refetches the active `issue` list queries.
 *
 * The events select only `id` and are used purely as a "something changed"
 * signal — the refetch goes back through `findIssues`, so what the list shows
 * is still decided by the read path's filters and authorization.
 *
 * Withheld without a workspace: the unscoped (global-admin) feed would receive
 * every workspace's events.
 */
export const useLiveIssueListRefresh = (
  workspaceId: string | undefined,
  enabled = true
) => {
  const queryClient = useQueryClient();
  const { logger } = useGrippContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (maxWaitRef.current) clearTimeout(maxWaitRef.current);
    debounceRef.current = null;
    maxWaitRef.current = null;
    // `throwOnError`, or a failed refetch only lands in the query's own state
    // and the catch below never sees it.
    queryClient
      .invalidateQueries(
        { queryKey: cacheKeys('issue').lists() },
        { throwOnError: true }
      )
      .catch((error: unknown) => {
        logger?.error(
          new Error(`Failed to refresh issue lists after a live event: ${String(error)}`)
        );
      });
  }, [queryClient, logger]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (maxWaitRef.current) clearTimeout(maxWaitRef.current);
    },
    []
  );

  const schedule = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flush, LIVE_REFRESH_DEBOUNCE_MS);
    if (!maxWaitRef.current) {
      maxWaitRef.current = setTimeout(flush, LIVE_REFRESH_MAX_WAIT_MS);
    }
  }, [flush]);

  const live = enabled && !!workspaceId;
  const variables = { filter: { workspace: { eq: workspaceId } } };

  useSubscription({ query: NEW_ISSUE, variables }, schedule, live);
  useSubscription({ query: UPDATED_ISSUE, variables }, schedule, live);
};

const NEW_ISSUE = `
  subscription liveNewIssue($filter: IssueSubscriptionFilter) {
    newIssue(filter: $filter) {
      id
    }
  }
`;

const UPDATED_ISSUE = `
  subscription liveUpdatedIssue($filter: IssueSubscriptionFilter) {
    updatedIssue(filter: $filter) {
      id
    }
  }
`;
